import type { Event, StackFrame } from '@sentry/react-native';
import { isNetworkError } from './networkErrorUtils';

export type WindowErrorDetails = {
    message?: string;
    filename?: string;
    lineno?: number;
    colno?: number;
};

const INTERCOM_REDUX_ERROR_RE = /(?:Minified Redux error #3|redux\.js\.org\/Errors\?code=3)/i;
const INTERCOM_WIDGET_FRAME_RE = /(?:js\.intercomcdn\.com|(?:^|[\s/])(?:app~tooltips|vendors~tooltips|tooltips)\.[\w-]+\.js|(?:^|[\s/])vendor\.99fe2e83\.js)/i;
const PAPERZILLA_SOURCE_FRAME_RE = /(?:app:\/\/\/|\/)(?:app|components|hooks|lib|utils)\//i;
const INPAGE_REMOVE_LISTENER_RE = /Cannot read properties of undefined \(reading 'removeListener'\)/i;
const INPAGE_EXTENSION_FRAME_RE = /(?:^|\/)inpage\.js(?:\s|$)/i;
const INTERCOM_NULL_DOCUMENT_RE = /Cannot read properties of null \(reading 'document'\)/i;
const INTERCOM_FRAME_MODERN_RE = /(?:js\.intercomcdn\.com\/|(?:^|\/))frame-modern\.[\w-]+\.js/i;
// CefSharp-based link scanners can reject promises with this .NET bridge signature.
const CEFSHARP_OBJECT_NOT_FOUND_RE = /Object Not Found Matching Id:\d+,\s*MethodName:[\w$]+,\s*ParamCount:\d+/i;

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null;
}

function joinText(values: unknown[]): string {
    return values
        .filter((value): value is string => typeof value === 'string' && value.length > 0)
        .join(' ');
}

function getErrorLikeStringProperty(
    errorLike: unknown,
    property: 'message' | 'name' | 'stack',
): string {
    if (!isRecord(errorLike)) {
        return '';
    }

    try {
        const value = errorLike[property];
        return typeof value === 'string' ? value : '';
    } catch {
        // Cross-origin browser objects can throw when their properties are read.
        return '';
    }
}

export function getErrorLikeMessage(errorLike: unknown): string {
    if (typeof errorLike === 'string') {
        return errorLike;
    }

    return getErrorLikeStringProperty(errorLike, 'message');
}

export function getErrorLikeStack(errorLike: unknown): string {
    return getErrorLikeStringProperty(errorLike, 'stack');
}

function getErrorLikeName(errorLike: unknown): string {
    return getErrorLikeStringProperty(errorLike, 'name');
}

function getFrameText(frame: StackFrame): string {
    return joinText([
        frame.filename,
        frame.abs_path,
        frame.module,
        frame.function,
        frame.context_line,
    ]);
}

function hasRetryExhaustedTag(value: unknown): boolean {
    return value === true
        || value === 'true'
        || value === 'network'
        || value === 'timeout';
}

export function isNonTerminalNetworkSentryEvent(event: Event): boolean {
    if (hasRetryExhaustedTag(event.tags?.retry_exhausted)) {
        return false;
    }

    const exceptionValues = event.exception?.values ?? [];
    const serializedException = isRecord(event.extra)
        && isRecord(event.extra.__serialized__)
        ? event.extra.__serialized__
        : undefined;
    const eventText = joinText([
        event.message,
        event.logentry?.message,
        ...exceptionValues.flatMap((exception) => [exception.type, exception.value]),
        serializedException?.message,
        serializedException?.code,
    ]);

    return isNetworkError({ message: eventText });
}

export function isKnownCefSharpObjectNotFoundWindowError(
    errorLike: unknown,
    details: WindowErrorDetails = {},
): boolean {
    const errorText = joinText([
        getErrorLikeName(errorLike),
        getErrorLikeMessage(errorLike),
        getErrorLikeStack(errorLike),
        details.message,
        details.filename,
    ]);

    return CEFSHARP_OBJECT_NOT_FOUND_RE.test(errorText);
}

export function isKnownCefSharpObjectNotFoundSentryEvent(event: Event): boolean {
    const exceptionValues = event.exception?.values ?? [];
    const exceptionText = joinText([
        event.message,
        event.logentry?.message,
        ...exceptionValues.flatMap((exception) => [exception.type, exception.value]),
    ]);

    return CEFSHARP_OBJECT_NOT_FOUND_RE.test(exceptionText);
}

export function isKnownIntercomReduxWindowError(
    errorLike: unknown,
    details: WindowErrorDetails = {},
): boolean {
    const errorText = joinText([
        getErrorLikeName(errorLike),
        getErrorLikeMessage(errorLike),
        getErrorLikeStack(errorLike),
        details.message,
        details.filename,
    ]);

    return INTERCOM_REDUX_ERROR_RE.test(errorText) && INTERCOM_WIDGET_FRAME_RE.test(errorText);
}

export function isKnownIntercomReduxSentryEvent(event: Event): boolean {
    const exceptionValues = event.exception?.values ?? [];
    const exceptionText = joinText([
        event.message,
        event.logentry?.message,
        ...exceptionValues.flatMap((exception) => [exception.type, exception.value]),
    ]);

    if (!INTERCOM_REDUX_ERROR_RE.test(exceptionText)) {
        return false;
    }

    const frameTexts = exceptionValues
        .flatMap((exception) => exception.stacktrace?.frames ?? [])
        .map(getFrameText);

    if (!frameTexts.some((frameText) => INTERCOM_WIDGET_FRAME_RE.test(frameText))) {
        return false;
    }

    return !frameTexts.some((frameText) => PAPERZILLA_SOURCE_FRAME_RE.test(frameText));
}

export function isKnownIntercomFrameModernWindowError(
    errorLike: unknown,
    details: WindowErrorDetails = {},
): boolean {
    const errorText = joinText([
        getErrorLikeName(errorLike),
        getErrorLikeMessage(errorLike),
        getErrorLikeStack(errorLike),
        details.message,
        details.filename,
    ]);

    return INTERCOM_NULL_DOCUMENT_RE.test(errorText) && INTERCOM_FRAME_MODERN_RE.test(errorText);
}

export function isKnownIntercomFrameModernSentryEvent(event: Event): boolean {
    const exceptionValues = event.exception?.values ?? [];
    const exceptionText = joinText([
        event.message,
        event.logentry?.message,
        ...exceptionValues.flatMap((exception) => [exception.type, exception.value]),
    ]);

    if (!INTERCOM_NULL_DOCUMENT_RE.test(exceptionText)) {
        return false;
    }

    const frameTexts = exceptionValues
        .flatMap((exception) => exception.stacktrace?.frames ?? [])
        .map(getFrameText);

    return frameTexts.some((frameText) => INTERCOM_FRAME_MODERN_RE.test(frameText));
}

export function isKnownBrowserExtensionInpageSentryEvent(event: Event): boolean {
    const exceptionValues = event.exception?.values ?? [];
    const exceptionText = joinText([
        event.message,
        event.logentry?.message,
        ...exceptionValues.flatMap((exception) => [exception.type, exception.value]),
    ]);

    if (!INPAGE_REMOVE_LISTENER_RE.test(exceptionText)) {
        return false;
    }

    const frameTexts = exceptionValues
        .flatMap((exception) => exception.stacktrace?.frames ?? [])
        .map(getFrameText);

    if (!frameTexts.some((frameText) => INPAGE_EXTENSION_FRAME_RE.test(frameText))) {
        return false;
    }

    return !frameTexts.some((frameText) => PAPERZILLA_SOURCE_FRAME_RE.test(frameText));
}
